/**
 * SCRUM-72: contexto único de "sucursal activa" para el admin — reemplaza el
 * patrón fragmentado de selectores de sucursal ad-hoc por pantalla (uno en
 * compras/ordenes, otro en compras/recepción, otro en ajustes de inventario,
 * otro en conteo físico...) por una sola fuente de verdad, mismo patrón que
 * `tenant-context.tsx` (Context API puro, sin librería de estado externa).
 *
 * `activeLocationId: null` significa "todas las sucursales" (alcance global)
 * — el valor por defecto para tenants single-location y para owners que no
 * eligieron una sucursal puntual. Nunca es la única barrera de seguridad:
 * cada mutación location-aware sigue validándose server-side vía
 * `assertSmLocationAccess` (SCRUM-64) sin importar lo que diga este contexto.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { apiGetJson } from "@/lib/sm-api";

export interface WorkspaceLocation {
  id: string;
  name: string;
  active: boolean;
  isDefault?: boolean;
}

export interface LocationContextValue {
  /** Sucursales activas del tenant. Vacío mientras carga. */
  locations: WorkspaceLocation[];
  /** null = "todas las sucursales" (alcance global). */
  activeLocationId: string | null;
  activeLocation: WorkspaceLocation | null;
  setActiveLocationId: (id: string | null) => void;
  /** true solo con 2+ sucursales activas — con 0 o 1 no tiene sentido mostrar un switcher. */
  isMultiLocation: boolean;
  loading: boolean;
}

const LocationContext = createContext<LocationContextValue | null>(null);

export function useLocationContext(): LocationContextValue {
  const ctx = useContext(LocationContext);
  if (!ctx) throw new Error("useLocationContext must be used inside LocationContextRoot");
  return ctx;
}

/** Safe variant — null fuera del provider (SSR, o componentes montados antes de que el contexto esté listo). */
export function useLocationContextSafe(): LocationContextValue | null {
  return useContext(LocationContext);
}

/** SCRUM-72: análogo a useWorkspaceTenantId — el id de sucursal activa (null = todas). */
export function useWorkspaceLocationId(): string | null {
  return useLocationContext().activeLocationId;
}

function storageKey(tenantId: string): string {
  return `sm:activeLocation:${tenantId}`;
}

export function LocationContextRoot({
  tenantId,
  children,
}: {
  tenantId: string;
  children: React.ReactNode;
}) {
  const [locations, setLocations] = useState<WorkspaceLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeLocationId, setActiveLocationIdState] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId) {
      setLocations([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    apiGetJson<WorkspaceLocation[]>("/api/locations", tenantId)
      .then((rows) => {
        if (cancelled) return;
        const active = rows.filter((l) => l.active);
        setLocations(active);
        const stored = localStorage.getItem(storageKey(tenantId));
        // Un id guardado que ya no existe/activo (sucursal borrada o de otro
        // tenant) se ignora — vuelve a "todas las sucursales", nunca rompe.
        const validStored = stored && active.some((l) => l.id === stored) ? stored : null;
        setActiveLocationIdState(validStored);
      })
      .catch(() => setLocations([]))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const setActiveLocationId = useCallback(
    (id: string | null) => {
      setActiveLocationIdState(id);
      if (id) localStorage.setItem(storageKey(tenantId), id);
      else localStorage.removeItem(storageKey(tenantId));
    },
    [tenantId],
  );

  const value = useMemo<LocationContextValue>(
    () => ({
      locations,
      activeLocationId,
      activeLocation: locations.find((l) => l.id === activeLocationId) ?? null,
      setActiveLocationId,
      isMultiLocation: locations.length >= 2,
      loading,
    }),
    [locations, activeLocationId, setActiveLocationId, loading],
  );

  return <LocationContext.Provider value={value}>{children}</LocationContext.Provider>;
}
